class MercadoPagoService {
  constructor(publicKey) {
    if (!publicKey) {
      console.warn('Mercado Pago public key is not configured');
    }
    this.publicKey = publicKey;
    this.initialized = false;
    this.mp = null;
  }

  initialize = async () => {
    if (this.initialized) return;
    if (!this.publicKey) {
      throw new Error('Mercado Pago public key is required');
    }
    
    try {
      // O SDK do Mercado Pago não precisa de inicialização via JS
      // A integração será feita via backend
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing Mercado Pago:', error);
      throw error;
    }
  };

  /**
   * Cria um checkout Pix com os dados do pedido
   * @param {Object} orderData - Dados do pedido
   * @returns {Promise<Object>} - Dados de pagamento com QR code
   */
  createPixPayment = async (orderData) => {
    try {
      await this.initialize();

      // Chamar backend para criar order e obter dados do Pix com QR code
      const response = await fetch('/api/mercadopago/create-preference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error('Erro ao criar ordem de pagamento');
      }

      const paymentData = await response.json();
      
      return {
        orderId: paymentData.order_id,
        paymentId: paymentData.payment_id,
        qrCode: paymentData.qr_code || paymentData.ticket_url, // Código PIX ou URL
        qr_code: paymentData.qr_code, // String do PIX para copiar
        qr_code_base64: paymentData.qr_code_base64, // Imagem QR code
        ticketUrl: paymentData.ticket_url,
        status: paymentData.status
      };
    } catch (error) {
      console.error('Error creating PIX payment:', error);
      throw error;
    }
  };

  /**
   * Verifica o status do pagamento
   * @param {String} paymentId - ID do pagamento no Mercado Pago
   * @returns {Promise<Object>} - Status e detalhes do pagamento
   */
  checkPaymentStatus = async (paymentId) => {
    try {
      const response = await fetch(
        `/api/mercadopago/payment-status/${paymentId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao verificar status do pagamento');
      }

      const paymentStatus = await response.json();

      return {
        status: paymentStatus.status, // approved, pending, rejected
        statusDetail: paymentStatus.status_detail,
        approved: paymentStatus.status === 'approved',
      };
    } catch (error) {
      console.error('Error checking payment status:', error);
      throw error;
    }
  };

  /**
   * Aguarda a confirmação do pagamento (polling)
   * @param {String} paymentId - ID do pagamento
   * @param {Number} maxAttempts - Máximo de tentativas
   * @param {Number} interval - Intervalo entre tentativas em ms
   * @returns {Promise<Boolean>} - true se pagamento aprovado
   */
  waitForPaymentConfirmation = async (
    paymentId,
    maxAttempts = 60, // 60 tentativas
    interval = 2000 // 2 segundos
  ) => {
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const checkPayment = async () => {
        try {
          const status = await this.checkPaymentStatus(paymentId);

          if (status.approved) {
            resolve(true);
            return;
          }

          if (status.status === 'rejected') {
            resolve(false);
            return;
          }

          attempts += 1;
          if (attempts >= maxAttempts) {
            resolve(false);
            return;
          }

          setTimeout(checkPayment, interval);
        } catch (error) {
          reject(error);
        }
      };

      checkPayment();
    });
  };
}

export default MercadoPagoService;
